import { Box, Container, Typography, styled, Link, alpha, useTheme, Theme, Divider } from "@mui/material";
import { crudGetAll, ReduxState, useAuthState } from "ra-core";
import { EventHandler, FC, SyntheticEvent, useState } from "react";
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import { match, Redirect, Link as RouterLink } from "react-router-dom";
import { Location } from 'history'
import sitemap, { SiteMapEntry } from '~/_docOut/sitemap'
import docs from '~/_docOut'
import Markdown, { Options } from 'react-markdown';
import remarkGfm from 'remark-gfm';
import { NavLink, Link as RouteLink } from 'react-router-dom'
import { useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import { READING_MODE_OFF, READING_MODE_ON } from "~/actions/readingMode";
import { PrismLight as SyntaxHighlighter } from 'react-syntax-highlighter';
import sql from 'react-syntax-highlighter/dist/esm/languages/prism/sql'
import json from 'react-syntax-highlighter/dist/esm/languages/prism/json'
import bash from 'react-syntax-highlighter/dist/esm/languages/prism/bash'
import powershell from 'react-syntax-highlighter/dist/esm/languages/prism/powershell'
import vsc_dark_plus from 'react-syntax-highlighter/dist/cjs/styles/prism/vsc-dark-plus';
import { sorts } from "~/utils/paramsWithGetMainList";
import { useCallback } from "react";
import { push } from "connected-react-router";
import { getStashedLocation, STASH_LOCATION_CLEAR, State } from "~/reducers/stashLocation";
import { BorderBottom } from "@mui/icons-material";


SyntaxHighlighter.registerLanguage('sql', sql)
SyntaxHighlighter.registerLanguage('json', json)
SyntaxHighlighter.registerLanguage('bash', bash)
SyntaxHighlighter.registerLanguage('powershell', powershell)


const MarkdownWrapper = styled(Box)(({theme}) => ({
  marginLeft: theme.spacing(7),
  color: theme.palette.text.primary,
  fontFamily: theme.typography.fontFamily,
  '& h1': {
    fontSize: theme.typography.h1.fontSize,
    fontWeight: theme.typography.fontWeightBold,
    lineHeight: theme.typography.h1.lineHeight,
    marginTop: theme.spacing(8),
    marginBottom: theme.spacing(6),
  },
  '& h2': {
    fontSize: theme.typography.h2.fontSize,
    fontWeight: theme.typography.fontWeightBold,
    lineHeight: theme.typography.h2.lineHeight,
    marginTop: theme.spacing(6),
    marginBottom: theme.spacing(3),
  },
  '& h3': {
    fontSize: theme.typography.h3.fontSize,
    fontWeight: theme.typography.fontWeightBold,
    lineHeight: theme.typography.h3.lineHeight,
    marginTop: theme.spacing(3),
    marginBottom: theme.spacing(3),
  },
  '& h4': {
    fontSize: theme.typography.h4.fontSize,
    fontWeight: theme.typography.fontWeightBold,
    lineHeight: theme.typography.h4.lineHeight,
    marginTop: theme.spacing(3),
    marginBottom: theme.spacing(3),
  },
  '& p': {
    fontSize: theme.typography.body1.fontSize,
    lineHeight: theme.typography.body1.lineHeight,
    marginBottom: theme.spacing(2)
  },
  '& li': {
    fontSize: theme.typography.body1.fontSize,
    lineHeight: theme.typography.body1.lineHeight,
    marginBottom: theme.spacing(1)
  },
  '& img': { maxWidth: '100%' },
  '& blockquote': { 
    backgroundColor: theme.palette.background.default, 
    margin: theme.spacing(2),
    padding: theme.spacing(2),
    borderRadius: theme.spacing(1),
  },
  '& table': {
    width: '100%',
    borderCollapse: 'collapse',
    borderSpacing: 0,
    borderRadius: 4,
    boxShadow: theme.shadows[2],
    '& th, td': {
      verticalAlign: 'inherit',
      textAlign: 'left',
      padding: '6px 16px',
      borderBottom: `1px solid ${theme.palette.divider}`,
      color: theme.palette.text.primary,
    },
    '& th': {
      padding: '16px',
      lineHeight: theme.typography.pxToRem(24),
      fontWeight: theme.typography.fontWeightMedium,
    },
  },
}))


const DocMenu = styled(Box)(({theme}) => ({
  paddingTop: theme.spacing(8),
  '& ul': { 
    listStyle: 'none',
    paddingInlineStart: 0,
  },
  '& li.menu': {
    marginBottom: theme.spacing(1),
    '& p': { display: 'flex', alignItems: 'center' },
    '& span.MuiTypography-root': { display: 'inline-flex', marginLeft: theme.spacing(0.5),
      transform: 'rotate(0)',
      transition: 'transform 0.3s ease',
    },
    '&.collapse': {
      height: 36, overflowY: 'hidden',
      '& span.MuiTypography-root': { color: theme.palette.action.disabledBackground, transform: 'rotate(180deg)' },
    },
    '& > button:hover span': { color: theme.palette.primary.light },
  },
  '& li > a': {
    position: 'relative',
    lineHeight: '1.8em',
    textDecoration: 'unset',
    '& > span': { 
      display: 'inline-block',
      width: 4,
      height: 25,
      position: 'absolute', 
    },
    '&.active': {
      color: alpha(theme.palette.text.primary, 0.9),
      fontWeight: theme.typography.h6.fontWeight,
      '& > span': {
        borderLeft: `4px solid ${theme.palette.info.light}`,
        top: -1,
      },
    },
    '&:hover': {
      textDecoration: 'unset',
      color: alpha(theme.palette.primary.main, 0.8),
    },
  },
}))

const renderDocsEntry = (ls: SiteMapEntry[], lvl: number, setDocPath:(x:number[])=>void, docPath: number[], pathIn?: number[]) => {
  const path = pathIn ? [...pathIn] : []
  const onPath = (i:number) => path.every((x, j) => docPath[j] === x) && docPath[path.length] === i
  return (
    <ul>
      {ls.map((entry, i) => (
        !entry.key ?
          <Box 
            component="li"
            key={entry.name} 
            className={`menu${onPath(i) ? '' : ' collapse'}`}
            sx={{ pl: 2 }}
          >
            <Link component="button" underline="none" onClick={() => setDocPath([...path, i])}>
              <Typography 
                component="p"
                variant="h6"
                color="textSecondary"
                sx={{mt: 1}}
              >
                {entry.name}
                <Typography variant="caption" color="InactiveCaptionText">
                  <ExpandLessIcon />
                </Typography>
              </Typography>
            </Link>
            {entry.subEntries && renderDocsEntry(entry.subEntries, lvl + 1, setDocPath, docPath, [...path, i])}
          </Box> :
          <Box
            component="li"
            key={entry.key}
            sx={{ pl: 2 }}
          >
            <Link 
              variant="body1" 
              underline="hover"
              color="textSecondary" 
              component={NavLink} 
              to={`/documents${entry.key}`}
            >
              <Box 
                component="span" 
                sx={{
                  left: theme => theme.spacing(-(2*lvl + 2)),
                  pl: theme => theme.spacing(2*lvl + 2),
                }} 
              >
              </Box>
              {entry.name}
            </Link>
          </Box>
      ))}
    </ul>
  )
}

const DocumentsLanding: FC<{
  match: match,
  location: Location,
}> = ({
  match,
  location,
}) => {
  const stashedLocation = useSelector<ReduxState, State>(getStashedLocation)
  const dispatch = useDispatch()
  useEffect(() => {
    dispatch({ type: READING_MODE_ON })
    return () => {
      dispatch({ type: READING_MODE_OFF })
      dispatch({ type: STASH_LOCATION_CLEAR })
    }
  }, [dispatch])
  
  const theme = useTheme()

  const handleDownload = useCallback((resId: string) => (e: SyntheticEvent) => {
    const i = resId.lastIndexOf('/')
    const filename = resId.substring(i + 1)
    dispatch(crudGetAll('downloadScripts', sorts(), { resId, filename }, 0))
  }, [dispatch])
  
  const [docPath, setDocPath] = useState([] as number[])
  const articlePath = location.pathname.replace('/documents', '')

  useEffect(() => {
    const check = (acc: {path:number[], finished:boolean}, t: SiteMapEntry, i:number):{path:number[], finished:boolean} => {
      if (acc.finished) { return acc }
      
      acc.path.push(i)
      if (t.key && t.key === articlePath) {
        acc.finished = true
        return acc
      } else if (t.subEntries && t.subEntries.length > 0) {
        for(var j in t.subEntries) {
          const acc1 = check(acc, t.subEntries[j], +j)
          if (acc1.finished) { return acc1 }
        }
      }
      acc.path.pop()
      return acc
    }
    const { path } = sitemap.reduce(check, {path:[], finished:false})
    setTimeout(() => { setDocPath(path) })
  }, [articlePath])

  const { loading, authenticated } = useAuthState()
  if (loading) return null

  if (!authenticated) {
    return (<Redirect to="/" />)
  }

  if (match.isExact) {
    const firstArticlePath = Object.keys(docs)[0]
    firstArticlePath && dispatch(push(`/documents${firstArticlePath}`))
  }

  const articleContent = (docs as any)[articlePath] || ''

  return (
    <>
      {stashedLocation && stashedLocation.location &&
        <Box sx={{ 
          width: '100vw', 
          height: 24, 
          bgcolor: alpha(theme.palette.secondary.main, 0.9), 
          position: 'fixed',
          zIndex: 1,
          display: 'flex',
          justifyContent: 'center'
        }}>
          <Link 
            underline="hover" 
            variant="caption" 
            sx={{cursor:'pointer', flex: '1 1 auto'}} 
            color={theme.palette.secondary.contrastText} 
            textAlign="center"
            component={RouterLink}
            to={stashedLocation?.location}
          >
            Back to previous location
          </Link>
        </Box>
      }
      <Box
        sx={{
          bgcolor: 'background.paper',
          overflowX: 'hidden',
        }}
      >
        <Container
          maxWidth="lg"
          sx={{
            mb: 40,
            minHeight: 'calc(100vh - 384px)',
          }}
        >
          <Box
            sx={{
              display: 'flex',
              flexDirection: 'row',
            }}
          >
            <Box
              role="main"
              component="article"
              sx={{
                flex: '1 1 auto',
                width: '200px',
              }}
            >
              <MarkdownWrapper>
                <Markdown remarkPlugins={[remarkGfm]}
                  children={articleContent} 
                  components={compontentsExtension(theme, handleDownload)}
                />
              </MarkdownWrapper>
            </Box>

            <Box
              role="navigation"
              sx={{
                flex: '0 0 300px',
                ml: 10,
              }}
            >
              <Box
                sx={{ 
                  bgcolor: 'background.default',
                  height: '100%',
                  overflowY: 'auto',
                  pl: 2,
                  pr: 99,
                  mr: -99,
                  position: 'fixed',
                }}
              >
                <DocMenu sx={{ pt: 6 }}>
                  {renderDocsEntry(sitemap, 0, setDocPath, docPath)}
                </DocMenu>
              </Box>
              <div>
              </div>
            </Box>
          </Box>
        </Container>
      </Box>
    </>
  )
}

export default DocumentsLanding;


const compontentsExtension: (theme: Theme, handleDownload: (resId: string) => EventHandler<SyntheticEvent>) => Options['components'] = (theme, handleDownload) => ({
  a: ({ node, href, children, ...props }) => {

    if (href) {
      if (href.startsWith('reference_doc#')) {
        const _href = href.replace('reference_doc#', '/documents/')
        return <Link component={RouteLink} to={_href}>{children}</Link>
      
      } else if (href.startsWith('download_res#')) {
        const resId = href.replace('download_res#', '')

        return <Link onClick={handleDownload(resId)} sx={{ cursor: 'pointer' }} >{children}</Link>
      
      } else {
        return <Link href={href} target="_blank">{children}</Link>
      }
    }
    
    return null
  },
  code: ({ node, children, sourcePosition, index, siblingCount, className, inline, ...props }) => {
    const match = /language-(\w+)/.exec(className || '')
    
    return !inline && match ? (
      <SyntaxHighlighter
        children={String(children).replace(/\n$/, '')}
        style={vsc_dark_plus}
        language={match[1]}
        PreTag="div"
        {...props as any}
      />
    ) : (
      <code className={className} {...props}>
        <Typography 
          variant="body1" 
          component="span" 
          color="textPrimary"
          sx={{
            bgcolor: theme => 
              theme.palette.mode === 'light'
              ? theme.palette.grey.A200
              : theme.palette.grey[800],
            py: '1px',
            px: '4px',
            borderRadius: '2px'
          }}
        >
          {children.map(x => {
            return typeof x === 'string' ? x.replaceAll('&bsol;', '\\') : x
          })}
        </Typography>
      </code>
    )
  },
  hr: () => {
    return <Divider />
  },

})

